import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Calendar,
  CalendarX,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  MoreHorizontal,
  Package,
  Pencil,
  Plus,
  Upload,
  User,
  Wrench,
} from 'lucide-react';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';
import { PatientTabIconButton } from '@/components/patient/PatientTabActions';
import { FormsResponseViewModal } from '@/components/patient/FormsResponseViewModal';
import { CollapsedToolRail, ToolsColumn, ToolsSection } from '@/components/journal/ToolsColumn';
import { TreatmentCourseStepJournal } from '@/components/patient/TreatmentCourseStepJournal';
import {
  StepSendQuestionnairePanel,
  type StepSendConfirmation,
} from '@/components/patient/course/StepSendQuestionnairePanel';
import { useCourseStepQuestionnaireAllowList } from '@/hooks/useCourseStepQuestionnaireAllowList';
import { useFormsTemplateList } from '@/hooks/useFormsTemplates';
import {
  patientFormsAssignmentsKey,
  useCancelQuestionnaire,
  useResendQuestionnaire,
  useSendQuestionnaire,
  type PatientFormsAssignment,
} from '@/hooks/useClinioForms';
import { questionnairePickerName } from '@/lib/questionnairePickerLabel';
import { canUndoStepQuestionnaireSend } from '@/lib/stepSendQuestionnaires';
import { treatmentCourseStaffViewQueryKey } from '@/hooks/useTreatmentCourses';
import { UploadFileSlideOver } from '@/components/documents/UploadFileSlideOver';
import {
  CourseBreadcrumb,
  CourseCount,
  CourseEmptyBlock,
  CourseIntervalStrip,
  CoursePlannedBookingChip,
  CourseStepMarker,
  CourseTabPanel,
  CourseTodayChip,
  CourseVisualChip,
} from '@/components/patient/course/CourseChrome';
import { useSaveTreatmentCourseStep } from '@/hooks/useTreatmentCourses';
import { useJournalEntries } from '@/hooks/useJournal';
import { useUploadClinicPatientDocument } from '@/hooks/useDocuments';
import { usePatientFileCategories } from '@/hooks/usePatientFileCategories';
import { useAuth } from '@/lib/auth';
import { readToolsColumnOpen, writeToolsColumnOpen } from '@/lib/journalWritePrefs';
import { patientJournalWritePath } from '@/lib/journalWritePath';
import { planStepJournalPersist } from '@/utils/journalPersist';
import {
  canBookBookedStep,
  canCancelBookedStep,
  canEditBookedCourse,
  draftFromBookedStep,
  stepDisplayTitle,
  type BookedStepDraft,
} from '@/lib/bookedTreatmentCourse';
import { courseQuestionnaireScores } from '@/lib/courseQuestionnaireScores';
import {
  canSendPlannedQuestionnaire,
  deriveBookedByChannel,
  deriveCourseStepVisual,
  deriveQuestionnaireState,
} from '@/lib/courseStepStatus';
import {
  formatCourseDate,
  formatCourseDateTime,
  formatCourseDateTimeRangeWithToday,
  isSameCourseDay,
} from '@/lib/courseDateFormat';
import {
  courseIntervalAfterLabel,
  coursePersonNames,
  courseQuestionnaireName,
  courseStepTitle,
  courseStepWindow,
  isQuestionnaireAllowedOnStep,
  questionnaireStatusLabel,
  questionnaireTimingLine,
  resolveCoursePersonName,
} from '@/lib/courseUiHelpers';
import type {
  StaffCourseQuestionnaire,
  StaffCourseStep,
  StaffTreatmentCourse,
} from '@/lib/treatmentCourseStaffView';
import { cn } from '@/utils/cn';

export function TreatmentCourseStepWorkspace({
  clinicId,
  course,
  stepId,
  canEdit,
  currentUserId,
  onOpenStep,
  onBack,
  onOpenList,
  onEditStep,
  onCancelStep,
  onRemoveStep,
  startJournal = false,
  patientName = '',
}: {
  clinicId: string;
  course: StaffTreatmentCourse;
  stepId: string;
  canEdit: boolean;
  currentUserId?: string | null;
  patientName?: string;
  onOpenStep: (stepId: string) => void;
  onBack: () => void;
  onOpenList?: () => void;
  onEditStep: (stepId: string) => void;
  onCancelStep: (step: StaffCourseStep) => void;
  onRemoveStep: (step: StaffCourseStep) => void;
  startJournal?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const saveStep = useSaveTreatmentCourseStep(clinicId);
  const canAmend = hasPermission(clinicId, 'journal', 'amend');
  const journalEntriesQuery = useJournalEntries(clinicId, { patientId: course.patient_id });
  const sendQuestionnaire = useSendQuestionnaire();
  const resendQuestionnaire = useResendQuestionnaire();
  const cancelQuestionnaire = useCancelQuestionnaire();
  const { data: templateLibrary } = useFormsTemplateList(clinicId);
  const formsTemplates = [...(templateLibrary?.platform ?? []), ...(templateLibrary?.clinic ?? [])];
  const [draft, setDraft] = useState<BookedStepDraft | null>(null);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [composeJournal, setComposeJournal] = useState(startJournal);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pickedTemplates, setPickedTemplates] = useState<string[]>([]);
  const [sendConfirmation, setSendConfirmation] = useState<StepSendConfirmation | null>(null);
  const [toolsOpen, setToolsOpen] = useState(() => readToolsColumnOpen(currentUserId));
  const journalRef = useRef<HTMLDivElement>(null);
  const upload = useUploadClinicPatientDocument();
  const { data: categories = [] } = usePatientFileCategories(clinicId);

  const steps = course.steps;
  const step = steps.find((row) => row.id === stepId) ?? steps[0] ?? null;
  const stepAllowList = useCourseStepQuestionnaireAllowList({
    clinicId,
    course,
    step,
  });
  const stepIndex = step ? steps.findIndex((row) => row.id === step.id) : -1;
  const previous = stepIndex > 0 ? steps[stepIndex - 1] : null;
  const next = stepIndex >= 0 && stepIndex < steps.length - 1 ? steps[stepIndex + 1] : null;
  const editable = Boolean(step && canEditBookedCourse(course.status, canEdit));
  const visual = step ? deriveCourseStepVisual(steps, step.id) : 'planned';
  const window = step ? courseStepWindow(steps, step) : null;

  useEffect(() => {
    setToolsOpen(readToolsColumnOpen(currentUserId));
  }, [currentUserId]);

  function setToolsColumnOpen(open: boolean) {
    writeToolsColumnOpen(currentUserId, open);
    setToolsOpen(open);
  }

  useEffect(() => {
    if (!step) {
      setDraft(null);
      return;
    }
    setDraft(draftFromBookedStep(step));
    setComposeJournal(false);
  }, [step?.id, step?.starts_at, step?.status]);

  useEffect(() => {
    if (!startJournal || !step) return;
    navigate(
      patientJournalWritePath(course.patient_id, {
        appointmentId: step.appointment_id,
        courseId: course.id,
        stepId: step.id,
        source: 'step',
        from: `${location.pathname}${location.search}`,
      }),
      { replace: true },
    );
  }, [course.id, course.patient_id, location.pathname, location.search, navigate, startJournal, step]);

  const stepNotes = useMemo(
    () =>
      (journalEntriesQuery.data ?? []).filter(
        (entry) =>
          Boolean(step?.appointment_id) &&
          entry.appointment_id === step?.appointment_id &&
          !entry.discarded_at,
      ),
    [journalEntriesQuery.data, step?.appointment_id],
  );
  const journalPlan = useMemo(
    () =>
      step?.appointment_id
        ? planStepJournalPersist(stepNotes, step.appointment_id)
        : { kind: 'create' as const },
    [step?.appointment_id, stepNotes],
  );

  useEffect(() => {
    if (journalPlan.kind === 'finalized' && composeJournal) {
      setComposeJournal(false);
    }
  }, [composeJournal, journalPlan.kind]);

  const bookedBy = deriveBookedByChannel(step?.appointment_source);
  const responseName = useMemo(() => {
    if (!step || !responseId) return undefined;
    const row = step.questionnaires.find((item) => item.forms_assignment_id === responseId);
    return row
      ? courseQuestionnaireName(row, i18n.language, t('treatment_courses.q_unnamed'))
      : undefined;
  }, [i18n.language, responseId, step, t]);

  async function sendNow(row: StaffCourseQuestionnaire) {
    if (!step || !editable) return;
    try {
      if (row.assignment_id && (row.assignment_status === 'sent' || row.assignment_status === 'reminded')) {
        await resendQuestionnaire.mutateAsync({
          assignment_id: row.assignment_id,
          patient_id: course.patient_id,
        });
        toast.success(t('treatment_courses.workspace_sent_now'));
        return;
      }
      const result = await sendQuestionnaire.mutateAsync({
        patient_id: course.patient_id,
        template_id: row.forms_template_id,
        metadata: {
          appointment_id: step.appointment_id,
          treatment_course_step_id: step.id,
          job_id: row.job_id,
          treatment_course_step_automation_id: row.automation_id,
        },
      });
      if (result.already_in_progress || result.already_sent) {
        toast(t('treatment_courses.workspace_send_already'));
        return;
      }
      toast.success(t('treatment_courses.workspace_sent_now'));
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message.trim() : '';
      toast.error(message || t('treatment_courses.patient_toast_error'));
    }
  }

  async function refreshStepAssignments() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: patientFormsAssignmentsKey(course.patient_id),
      }),
      queryClient.invalidateQueries({
        queryKey: treatmentCourseStaffViewQueryKey(clinicId, course.id),
      }),
    ]);
  }

  function clinioIdsForFormsAssignments(formsAssignmentIds: string[]): string[] {
    const cached = queryClient.getQueryData<PatientFormsAssignment[]>(
      patientFormsAssignmentsKey(course.patient_id),
    );
    const fromCache = (cached ?? [])
      .filter((row) => formsAssignmentIds.includes(row.forms_assignment_id))
      .map((row) => row.id);
    if (fromCache.length > 0) return fromCache;
    return (step?.questionnaires ?? [])
      .filter(
        (row) =>
          Boolean(row.assignment_id) &&
          Boolean(row.forms_assignment_id) &&
          formsAssignmentIds.includes(row.forms_assignment_id!),
      )
      .map((row) => row.assignment_id as string);
  }

  async function sendExtra(ids: string[]) {
    if (!step || !editable || ids.length === 0) return;
    const names: string[] = [];
    const formsAssignmentIds: string[] = [];
    const sentIds: string[] = [];
    try {
      for (const templateId of ids) {
        if (!isQuestionnaireAllowedOnStep(templateId, stepAllowList.ids)) {
          toast.error(t('treatment_courses.step_send_not_allowed'));
          continue;
        }
        const result = await sendQuestionnaire.mutateAsync({
          patient_id: course.patient_id,
          template_id: templateId,
          metadata: {
            appointment_id: step.appointment_id,
            treatment_course_step_id: step.id,
          },
        });
        if (result.already_in_progress || result.already_sent) {
          toast(t('treatment_courses.workspace_send_already'));
          continue;
        }
        const template = formsTemplates.find((item) => item.id === templateId);
        names.push(
          template
            ? questionnairePickerName(template, i18n.language, t('treatment_courses.q_unnamed'))
            : t('treatment_courses.q_unnamed'),
        );
        sentIds.push(templateId);
        if (result.assignment_id) formsAssignmentIds.push(result.assignment_id);
      }
      if (names.length === 0) return;
      await refreshStepAssignments();
      setPickedTemplates([]);
      setSendConfirmation({
        templateIds: sentIds,
        names,
        formsAssignmentIds,
        assignmentIds: clinioIdsForFormsAssignments(formsAssignmentIds),
      });
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message.trim() : '';
      toast.error(message || t('treatment_courses.patient_toast_error'));
    }
  }

  async function undoExtra() {
    if (!sendConfirmation) return;
    try {
      let assignmentIds = sendConfirmation.assignmentIds.filter(Boolean);
      if (assignmentIds.length === 0) {
        await refreshStepAssignments();
        assignmentIds = clinioIdsForFormsAssignments(sendConfirmation.formsAssignmentIds);
      }
      if (assignmentIds.length === 0) {
        toast.error(t('treatment_courses.patient_toast_error'));
        return;
      }
      for (const assignmentId of assignmentIds) {
        await cancelQuestionnaire.mutateAsync({
          assignment_id: assignmentId,
          patient_id: course.patient_id,
        });
      }
      setSendConfirmation(null);
      await refreshStepAssignments();
    } catch (err) {
      console.error(err);
      toast.error(t('treatment_courses.patient_toast_error'));
    }
  }

  if (!step || !draft) {
    return (
      <Banner
        tone="error"
        title={t('treatment_courses.patient_pick_step')}
        action={
          <Button type="button" variant="secondary" onClick={onBack}>
            {t('treatment_courses.crumb_all')}
          </Button>
        }
      />
    );
  }

  const previousScores = previous
    ? previous.questionnaires.flatMap((row) =>
        courseQuestionnaireScores(row.score).map((score) => ({
          ...score,
          name: courseQuestionnaireName(row, i18n.language, t('treatment_courses.q_unnamed')),
          at: row.completed_at,
        })),
      )
    : [];
  const previousMissingNote = Boolean(
    previous && !previous.has_journal_note && previous.status === 'completed',
  );
  const sendUndoable = Boolean(
    sendConfirmation &&
      sendConfirmation.formsAssignmentIds.some((id) => {
        const row = step.questionnaires.find((item) => item.forms_assignment_id === id);
        return !row || canUndoStepQuestionnaireSend(row.assignment_status);
      }),
  );

  return (
    <CourseTabPanel testId="treatment-course-step-workspace">
      <div className="fl-top">
        <CourseBreadcrumb
          items={[
            { label: t('treatment_courses.crumb_all'), onClick: onOpenList ?? onBack },
            {
              label: course.template_name || t('treatment_courses.summary_untitled'),
              onClick: onBack,
            },
            { label: t('treatment_courses.step_n', { n: step.sort_order }) },
          ]}
        />
        <div className="stepper" role="navigation" aria-label={t('treatment_courses.section_steps')}>
          <button
            type="button"
            className="sb arr"
            disabled={!previous}
            aria-label={t('treatment_courses.workspace_prev')}
            onClick={() => previous && onOpenStep(previous.id)}
          >
            <ChevronLeft className="i sm" />
          </button>
          {steps.map((row) => (
            <button
              key={row.id}
              type="button"
              className="sb"
              aria-current={row.id === step.id ? 'step' : undefined}
              aria-label={t('treatment_courses.step_of_total', {
                n: row.sort_order,
                total: steps.length,
              })}
              onClick={() => onOpenStep(row.id)}
            >
              <CourseStepMarker visual={deriveCourseStepVisual(steps, row.id)} size="sm" />
              {row.sort_order}
            </button>
          ))}
          <span className="lbl3 num">
            {t('treatment_courses.step_of_total', { n: step.sort_order, total: steps.length })}
          </span>
          <button
            type="button"
            className="sb arr"
            disabled={!next}
            aria-label={t('treatment_courses.workspace_next')}
            onClick={() => next && onOpenStep(next.id)}
          >
            <ChevronRight className="i sm" />
          </button>
        </div>
      </div>

      <div className="tf-h wrapm">
        <div className="hgrp">
          <div className="hl">
            <h2 id="h-fl">{courseStepTitle(step, t('treatment_courses.step_no_service'))}</h2>
            <CourseVisualChip visual={visual} />
            <CoursePlannedBookingChip visual={visual} startsAt={step.starts_at} />
            {isSameCourseDay(step.starts_at) ? <CourseTodayChip /> : null}
          </div>
        </div>
        <div className="r">
          {editable ? (
            <Button type="button" variant="secondary" onClick={() => onEditStep(step.id)}>
              <Pencil className="i sm" aria-hidden />
              {t('treatment_courses.edit_step')}
            </Button>
          ) : null}
          {editable ? (
            <MenuPopover
              title={t('treatment_courses.step_menu')}
              trigger={
                <PatientTabIconButton label={t('treatment_courses.step_menu')}>
                  <MoreHorizontal className="i" />
                </PatientTabIconButton>
              }
            >
              {canCancelBookedStep(step.status) ? (
                <MenuItem onClick={() => onCancelStep(step)}>
                  {t('treatment_courses.patient_cancel_step')}
                </MenuItem>
              ) : null}
              {steps.length > 1 ? (
                <MenuItem danger onClick={() => onRemoveStep(step)}>
                  {t('treatment_courses.remove_step')}
                </MenuItem>
              ) : null}
            </MenuPopover>
          ) : null}
          {editable && journalPlan.kind === 'finalized' && canAmend ? (
            <Button
              type="button"
              onClick={() =>
                navigate(
                  patientJournalWritePath(course.patient_id, {
                    appointmentId: step?.appointment_id,
                    courseId: course.id,
                    stepId: step?.id,
                    entryId: journalPlan.kind === 'finalized' ? journalPlan.entryId : undefined,
                    amend: true,
                    source: 'step',
                    from: `${location.pathname}${location.search}`,
                  }),
                )
              }
            >
              <Pencil className="i sm" aria-hidden />
              {t('journal.add_correction')}
            </Button>
          ) : null}
          {editable && journalPlan.kind !== 'finalized' ? (
            <Button
              type="button"
              onClick={() =>
                navigate(
                  patientJournalWritePath(course.patient_id, {
                    appointmentId: step?.appointment_id,
                    courseId: course.id,
                    stepId: step?.id,
                    entryId: journalPlan.kind === 'update' ? journalPlan.entryId : undefined,
                    source: 'step',
                    from: `${location.pathname}${location.search}`,
                  }),
                )
              }
            >
              <Pencil className="i sm" aria-hidden />
              {journalPlan.kind === 'update'
                ? t('treatment_courses.continue_journal')
                : t('treatment_courses.write_journal')}
            </Button>
          ) : null}
        </div>
      </div>

      <section className="band">
        <div>
          <div className="k">
            <Calendar className="i xs" aria-hidden />
            {t('treatment_courses.tab_appointment')}
            {bookedBy ? ` · ${t(`treatment_courses.booked_by_${bookedBy}`)}` : ''}
          </div>
          <div className="when num">
            {step.starts_at
              ? formatCourseDateTimeRangeWithToday(
                  step.starts_at,
                  step.ends_at,
                  i18n.language,
                  t('treatment_courses.today_chip'),
                )
              : t('treatment_courses.book_not_booked')}
          </div>
          <div className="facts2 num">
            <span>
              <Clock className="i sm" aria-hidden />
              {t('treatment_courses.duration_minutes', { minutes: step.duration_minutes })}
            </span>
            <span>
              <User className="i sm" aria-hidden />
              {coursePersonNames(step.practitioners) || t('appointments.unassigned')}
            </span>
            <span>
              <Package className="i sm" aria-hidden />
              {coursePersonNames(step.resources) || t('treatment_courses.book_no_resources')}
            </span>
          </div>
        </div>
        {editable && step.appointment_id ? (
          <div className="acts">
            <Button type="button" variant="secondary" onClick={() => onEditStep(step.id)}>
              <Calendar className="i sm" aria-hidden />
              {t('treatment_courses.move_time')}
            </Button>
            {canCancelBookedStep(step.status) ? (
              <Button type="button" variant="secondary" onClick={() => onCancelStep(step)}>
                <CalendarX className="i sm" aria-hidden />
                {t('treatment_courses.cancel_time')}
              </Button>
            ) : null}
          </div>
        ) : editable && canBookBookedStep(step.status) ? (
          <div className="acts">
            <Button type="button" onClick={() => onEditStep(step.id)}>
              <Calendar className="i sm" aria-hidden />
              {t('treatment_courses.book_time')}
            </Button>
          </div>
        ) : null}
        {step.sort_order > 1 && step.interval_value > 0 ? (
          <CourseIntervalStrip
            label={courseIntervalAfterLabel(step, step.sort_order - 1, t)}
            window={window}
            startsAt={step.starts_at}
          />
        ) : null}
      </section>

      <div className={cn('sv-grid', !toolsOpen && 'tools-shut')}>
        <div className="sv-main">
          <section ref={journalRef} className="pane">
            <div className="pane-h">
              <h3>{t('treatment_courses.overview_journal')}</h3>
              <span className={cn('chip', step.has_journal_note ? 'chip-green' : 'chip-neutral')}>
                {step.has_journal_note
                  ? t('treatment_courses.journal_done')
                  : t('treatment_courses.journal_not_started')}
              </span>
            </div>
            <TreatmentCourseStepJournal
              clinicId={clinicId}
              patientId={course.patient_id}
              appointmentId={step.appointment_id}
              courseId={course.id}
              stepId={step.id}
              currentUserId={currentUserId}
              defaultTitle={courseStepTitle(step, t('treatment_courses.step_no_service'))}
              emptyWhen={
                step.starts_at
                  ? formatCourseDateTime(step.starts_at, i18n.language)
                  : undefined
              }
            />
            {previousMissingNote ? (
              <div className="warn-inline">
                <svg className="i sm" viewBox="0 0 24 24" aria-hidden>
                  <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                </svg>
                <span>
                  {t('treatment_courses.previous_journal_missing', {
                    n: previous?.sort_order,
                    name:
                      resolveCoursePersonName(previous?.practitioners[0]?.name) ||
                      t('treatment_courses.none'),
                  })}{' '}
                  <button type="button" className="link" onClick={() => previous && onOpenStep(previous.id)}>
                    {t('treatment_courses.open_step_n', { n: previous?.sort_order })}
                  </button>
                </span>
              </div>
            ) : null}
          </section>

          <section className="pane">
            <div className="pane-h">
              <h3>{t('treatment_courses.tab_questionnaires')}</h3>
              <CourseCount value={step.questionnaires.length} />
              {editable ? (
                <div className="r">
                  <Button type="button" variant="tertiary" size="sm" onClick={() => onEditStep(step.id)}>
                    <Plus className="i sm" aria-hidden />
                    {t('treatment_courses.send_questionnaire')}
                  </Button>
                </div>
              ) : null}
            </div>
            {step.questionnaires.length === 0 ? (
              <CourseEmptyBlock
                icon={<Plus className="i" />}
                title={t('treatment_courses.workspace_no_questionnaires')}
              />
            ) : (
              step.questionnaires.map((row) => {
                const q = deriveQuestionnaireState({
                  jobStatus: row.job_status,
                  scheduledFor: row.scheduled_for,
                  attemptedAt: row.attempted_at,
                  jobReason: row.job_reason,
                  assignmentStatus: row.assignment_status,
                  sentAt: row.sent_at,
                  completedAt: row.completed_at,
                });
                const name = courseQuestionnaireName(
                  row,
                  i18n.language,
                  t('treatment_courses.q_unnamed'),
                );
                const scores = courseQuestionnaireScores(row.score);
                const prev = previous?.questionnaires.find(
                  (item) => item.forms_template_id === row.forms_template_id,
                );
                const prevScores = courseQuestionnaireScores(prev?.score);
                const automation = step.automations.find(
                  (item) =>
                    item.forms_template_id === row.forms_template_id && item.phase === row.phase,
                );
                return (
                  <div
                    key={`${row.automation_id ?? row.forms_assignment_id ?? row.forms_template_id}-${row.phase}`}
                    className={cn(
                      'qrow',
                      sendConfirmation?.templateIds.includes(row.forms_template_id) && 'new',
                    )}
                  >
                    <div>
                      <div className="qk">
                        {sendConfirmation?.templateIds.includes(row.forms_template_id)
                          ? t('treatment_courses.step_send_during', {
                              name:
                                coursePersonNames(
                                  step.practitioners.filter((person) => person.id === currentUserId),
                                ) ||
                                coursePersonNames(step.practitioners) ||
                                t('treatment_courses.none'),
                            })
                          : questionnaireTimingLine(
                              {
                                phase: row.phase,
                                offsetMinutes: automation?.offset_minutes,
                                sentAt: row.sent_at,
                                scheduledFor: row.scheduled_for,
                              },
                              t,
                            )}
                      </div>
                      <div className="qt">
                        {name}{' '}
                        <span
                          className={cn(
                            'chip',
                            q.state === 'completed'
                              ? 'chip-green'
                              : q.state === 'sent'
                                ? 'chip-teal'
                                : 'chip-neutral',
                          )}
                        >
                          {q.state === 'completed' ? <Check className="i xs" aria-hidden /> : <Calendar className="i xs" aria-hidden />}
                          {questionnaireStatusLabel(q.state, t)}
                        </span>
                      </div>
                      {sendConfirmation?.templateIds.includes(row.forms_template_id) ? (
                        <div className="qm num">
                          {t('treatment_courses.step_send_awaiting', {
                            when: formatCourseDateTime(
                              row.sent_at ?? new Date().toISOString(),
                              i18n.language,
                            ),
                          })}
                        </div>
                      ) : q.at ? (
                        <div className="qm num">
                          {q.state === 'completed'
                            ? t('treatment_courses.q_state_completed_at', {
                                when: formatCourseDateTime(q.at, i18n.language),
                              }).replace(/^Udfyldt|^Completed/, t('treatment_courses.q_answered'))
                            : t(`treatment_courses.q_state_${q.state}_at`, {
                                when: formatCourseDate(q.at, i18n.language),
                              })}
                        </div>
                      ) : null}
                    </div>
                    {row.forms_assignment_id ? (
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => setResponseId(row.forms_assignment_id)}
                      >
                        <Eye className="i sm" aria-hidden />
                        {t('treatment_courses.workspace_open_answers')}
                      </Button>
                    ) : editable &&
                      canSendPlannedQuestionnaire(q.state, row.job_status, row.attempted_at) ? (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={sendQuestionnaire.isPending || saveStep.isPending}
                        onClick={() => void sendNow(row)}
                      >
                        {t('treatment_courses.workspace_send_now')}
                      </Button>
                    ) : null}
                    {scores.length > 0 ? (
                      <div className="scores num">
                        {scores.map((score, index) => {
                          const earlier = prevScores[index];
                          const delta = earlier ? score.value - earlier.value : null;
                          return (
                            <div key={score.label} className="score">
                              <span className="l">
                                {/physical|fysisk/i.test(score.label)
                                  ? t('treatment_courses.score_physical')
                                  : /mental/i.test(score.label)
                                    ? t('treatment_courses.score_mental')
                                    : score.label}
                              </span>
                              <span className="v">{score.value}</span>
                              {delta != null ? (
                                <span className={cn('d', delta === 0 && 'n')}>
                                  {delta === 0
                                    ? t('treatment_courses.score_unchanged', { n: previous?.sort_order })
                                    : t('treatment_courses.score_delta', {
                                        delta: `${delta > 0 ? '+' : ''}${delta}`,
                                        n: previous?.sort_order,
                                        previous: earlier.value,
                                      })}
                                </span>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </section>
        </div>

        {!toolsOpen ? (
          <CollapsedToolRail
            testId="course-step-tools"
            label={t('journal.tools')}
            icon={<Wrench className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
            onClick={() => setToolsColumnOpen(true)}
          />
        ) : (
          <ToolsColumn
            className="fl-tools"
            scroll={false}
            testId="course-step-tools"
            title={t('journal.tools')}
            onCollapse={() => setToolsColumnOpen(false)}
            collapseLabel={t('journal.collapse_tools')}
          >
          {editable ? (
            <StepSendQuestionnairePanel
              variant="tools"
              templates={formsTemplates}
              allowedIds={stepAllowList.ids}
              allowSource={stepAllowList.source}
              allowListPending={stepAllowList.isLoading}
              allowListError={stepAllowList.isError}
              questionnaires={step.questionnaires}
              picked={pickedTemplates}
              onPickedChange={setPickedTemplates}
              patientName={patientName || t('treatment_courses.q_unnamed')}
              canSend={editable}
              sending={sendQuestionnaire.isPending}
              confirmation={sendConfirmation}
              onSend={(ids) => void sendExtra(ids)}
              onUndo={() => void undoExtra()}
              undoPending={cancelQuestionnaire.isPending}
              language={i18n.language}
              onViewResponse={setResponseId}
            />
          ) : null}
          {previous ? (
            <ToolsSection
              title={t('treatment_courses.from_step', { n: previous.sort_order })}
              icon={<Calendar className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
            >
              <ul className="prevl">
                <li>
                  <b>{stepDisplayTitle(previous, t('treatment_courses.step_no_service'))}</b>
                  <div className="m">
                    {formatCourseDateTime(previous.starts_at, i18n.language)}
                    {coursePersonNames(previous.practitioners) ? ` · ${coursePersonNames(previous.practitioners)}` : ''}
                  </div>
                </li>
                <li>
                  <span className={cn('chip', previous.has_journal_note ? 'chip-green' : 'chip-amber')}>
                    {previous.has_journal_note ? (
                      <Check className="i xs" aria-hidden />
                    ) : (
                      <svg className="i xs" viewBox="0 0 24 24" aria-hidden>
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
                        <path d="M12 9v4" />
                        <path d="M12 17h.01" />
                      </svg>
                    )}
                    {previous.has_journal_note
                      ? t('treatment_courses.journal_done')
                      : t('treatment_courses.action_journal')}
                  </span>
                </li>
                {previousScores.slice(0, 1).map((score) => (
                  <li key={score.label}>
                    {score.name}{' '}
                    {/mental/i.test(score.label)
                      ? t('treatment_courses.score_mental')
                      : /physical|fysisk/i.test(score.label)
                        ? t('treatment_courses.score_physical')
                        : score.label}{' '}
                    <b>{score.value}</b>
                    {score.at ? (
                      <div className="m">
                        {t('treatment_courses.q_state_completed_at', {
                          when: formatCourseDate(score.at, i18n.language),
                        }).replace(/^Udfyldt|^Completed/, t('treatment_courses.q_answered'))}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </ToolsSection>
          ) : null}
          <ToolsSection
            title={t('treatment_courses.workspace_files')}
            icon={<FileText className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
          >
            <p className="mini-e">{t('treatment_courses.files_empty')}</p>
            <Button type="button" variant="secondary" size="sm" onClick={() => setUploadOpen(true)}>
              <Upload className="i sm" aria-hidden />
              {t('treatment_courses.add_file')}
            </Button>
          </ToolsSection>
          <ToolsSection
            title={t('treatment_courses.practitioners_and_resources')}
            icon={<User className="h-4 w-4" strokeWidth={1.75} aria-hidden />}
          >
            <dl className="dl2">
              <div>
                <dt>{t('treatment_courses.tab_practitioners')}</dt>
                <dd>
                  {coursePersonNames(step.practitioners) || t('treatment_courses.none')}
                </dd>
              </div>
              <div>
                <dt>{t('treatment_courses.tab_resources')}</dt>
                <dd className={step.resources.length ? undefined : 'muted'}>
                  {coursePersonNames(step.resources) || t('treatment_courses.none')}
                </dd>
              </div>
            </dl>
          </ToolsSection>
          </ToolsColumn>
        )}
      </div>

      {sendConfirmation && sendConfirmation.names.length > 0 ? (
        <div className="fl-snack" role="status">
          <div className="grow">
            <b>
              {t('treatment_courses.step_send_ok_title', {
                name: sendConfirmation.names.join(', '),
                patient: patientName || t('treatment_courses.q_unnamed'),
              })}
            </b>
            <span>{t('treatment_courses.step_send_ok_via')}</span>
          </div>
          {sendUndoable ? (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              disabled={cancelQuestionnaire.isPending}
              onClick={() => void undoExtra()}
            >
              {t('treatment_courses.step_send_undo')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {responseId ? (
        <FormsResponseViewModal
          clinicId={clinicId}
          formsAssignmentId={responseId}
          templateName={responseName}
          onClose={() => setResponseId(null)}
        />
      ) : null}

      <UploadFileSlideOver
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        uploading={upload.isPending}
        categories={categories}
        onUpload={async (files, categoryId, documentDate) => {
          for (const file of files) {
            await upload.mutateAsync({
              clinicId,
              patientId: course.patient_id,
              file,
              categoryId,
              documentDate,
            });
          }
        }}
      />
    </CourseTabPanel>
  );
}
